import { Outlet } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useThemeMode } from "@context/shared/ThemeContext";
import { useAuth } from "@context/shared/AuthContext";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "@mui/material/styles";

import { AdminNav } from "../navigation/admin-nav";
import { TopNav } from "../navigation/top-nav";
import { SystemBannerSlot } from "../navigation/system-banner-slot";
import { useMobileNav } from "../navigation/use-mobile-nav";
import { COMPONENTS } from "@theme/admin";
import { FormDirtyProvider } from "../../../context/admin/FormDirty";
import PropTypes from 'prop-types';

const AdminLayoutRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: COMPONENTS.SIDE_NAV_WIDTH,
  },
}));

const AdminLayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

const AdminLayout = () => {
  const { setAdminTheme } = useThemeMode();
  // Only `fetchFeatures` is used here (it loads AuthContext.features that drives
  // route generation). The NAV's features come from AdminNav's own store, so a
  // feature toggle refreshing the menu never re-renders this layout or the page.
  const { fetchFeatures } = useAuth();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const mobileNav = useMobileNav();

  const fetchFeaturesCallback = useMemo(
    () => () => fetchFeatures(),
    [fetchFeatures]
  );

  useEffect(() => {
    setAdminTheme();
  }, [setAdminTheme]);

  // Fetch features when admin layout mounts or on page refresh
  useEffect(() => {
    fetchFeaturesCallback();
  }, [fetchFeaturesCallback]);

  return (
    <FormDirtyProvider>
      <SystemBannerSlot />
      <TopNav onMobileNavOpen={mobileNav.handleOpen} />
      <AdminNav lgUp={lgUp} mobileNav={mobileNav} />

      <AdminLayoutRoot>
        <AdminLayoutContainer>
          <Outlet />
        </AdminLayoutContainer>
      </AdminLayoutRoot>
    </FormDirtyProvider>
  );
};

export default AdminLayout;

AdminLayoutRoot.propTypes = {
  theme: PropTypes.shape({
    breakpoints: PropTypes.any,
  }),
};
